import { Link } from "react-router-dom";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useLang } from "@/context/LanguageContext";
import { PageHero } from "@/components/common/PageHero";
import { CTASection } from "@/components/common/CTASection";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ACCOUNTS = [
  { name: "Standard", desc: "A straightforward account suited to most traders, typically commission-free with spreads set by the broker." },
  { name: "Raw Spread", desc: "Tighter raw spreads with a commission per trade — favoured by active and cost-sensitive traders." },
  { name: "Advanced / Professional", desc: "Enhanced conditions for experienced or higher-volume traders, subject to eligibility." },
  { name: "Islamic / Swap-Free", desc: "Swap-free structure designed to comply with Islamic finance principles." },
  { name: "Corporate", desc: "Accounts for businesses and institutional clients with tailored requirements." },
];

const ROWS = [
  ["Typical use", "Most traders", "Active traders", "Experienced", "Swap-free", "Businesses"],
  ["Commission", "Set by broker", "Per-trade", "Set by broker", "Set by broker", "Negotiated"],
  ["Swap-free option", "—", "—", "—", "Yes", "—"],
  ["Eligibility checks", "Standard", "Standard", "Enhanced", "Standard", "Corporate KYC"],
];

export default function AccountSolutions() {
  const { L } = useLang();
  const P = L.pages.accounts;
  usePageSeo("accounts");
  return (
    <>
      <PageHero
        overline={P.overline}
        title={P.title}
        description={P.desc}
        breadcrumbs={[{ label: L.common.home, to: "/" }, { label: L.nav.sub.accounts[0] }]}
      />

      <section className="py-20 lg:py-28">
        <div className="container-tight grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {ACCOUNTS.map((a) => (
            <div key={a.name} className="rounded-xl border border-hair bg-white p-6 shadow-sm">
              <h3 className="font-heading text-lg font-semibold text-ink">{a.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-subtle">{a.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-canvas py-20 lg:py-28">
        <div className="container-tight">
          <h2 className="font-heading text-3xl font-semibold text-ink">Comparison</h2>
          <div className="mt-8 overflow-x-auto rounded-xl border border-hair bg-white shadow-sm">
            <Table className="min-w-[720px]">
              <TableHeader>
                <TableRow className="bg-navy hover:bg-navy">
                  <TableHead className="text-white">Feature</TableHead>
                  {ACCOUNTS.map((a) => <TableHead key={a.name} className="text-center text-white">{a.name}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {ROWS.map((r, i) => (
                  <TableRow key={i} className={i % 2 ? "bg-canvas" : ""}>
                    <TableCell className="font-medium text-ink">{r[0]}</TableCell>
                    {r.slice(1).map((v, j) => <TableCell key={j} className="text-center text-sm text-subtle">{v}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="mt-4 text-xs text-subtle">MENAGroupFX does not set spreads, leverage or minimum deposits. These are determined by the brokerage partner and vary by jurisdiction and eligibility.</p>
          <Button asChild className="mt-8 bg-finblue hover:bg-finblueDark"><Link to="/broker-partners">{P.cta}</Link></Button>
        </div>
      </section>

      <CTASection />
    </>
  );
}
